import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Copy, RefreshCw, Save, FileDown, Archive, Eraser } from "lucide-react";
import { format } from "date-fns";

export default function AdminPanel() {
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [count, setCount] = useState(10);
  const [maxUses, setMaxUses] = useState(1);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [settings, setSettings] = useState({ etsy_url: "", receipt_sender_email: "" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [allEvents, setAllEvents] = useState([]);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [filter, setFilter] = useState("");

  const loadAllEvents = async () => {
    setCleanupLoading(true);
    try {
      const { data } = await api.get("/admin/events/all");
      setAllEvents(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Failed to load events"); }
    finally { setCleanupLoading(false); }
  };

  const adminDeleteEvent = async (id, title) => {
    if (!window.confirm(`Permanently delete "${title}"?\nThis removes the event, all its RSVPs and guestbook entries.`)) return;
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success("Event deleted");
      setAllEvents((es) => es.filter((e) => e.id !== id));
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Delete failed"); }
  };

  const wipeUnusedCodes = async () => {
    if (!window.confirm("Delete EVERY unused redemption code?\nAlready-redeemed codes will be preserved.")) return;
    try {
      const { data } = await api.delete("/admin/codes/unused/all");
      toast.success(`Deleted ${data.deleted} unused codes`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Wipe failed"); }
  };

  const load = async () => {
    setListLoading(true);
    try {
      const [c, s, st] = await Promise.all([
        api.get("/admin/codes"),
        api.get("/admin/stats"),
        api.get("/admin/settings"),
      ]);
      setCodes(c.data); setStats(s.data);
      setSettings({ etsy_url: st.data.etsy_url || "", receipt_sender_email: st.data.receipt_sender_email || "" });
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setListLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/admin/settings", settings);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSavingSettings(false); }
  };

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/codes/generate", { count: parseInt(count) || 1, max_uses: parseInt(maxUses) || 1, note });
      toast.success(`Generated ${data.length} code${data.length === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this code? It will no longer be redeemable.")) return;
    try { await api.delete(`/admin/codes/${id}`); toast.success("Code revoked"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const copy = (code) => { navigator.clipboard.writeText(code); toast.success("Copied"); };

  const copyAll = () => {
    const all = codes.map((c) => c.code).join("\n");
    navigator.clipboard.writeText(all);
    toast.success(`Copied ${codes.length} codes`);
  };

  return (
    <div className="min-h-screen bg-[#FBF9F4]" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Admin · Codes studio</div>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-5xl mt-2 font-light tracking-tight">Codes.</h1>

        {/* Stats */}
        {stats && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mt-10">
              {[
                { k: "total_codes", l: "Codes", },
                { k: "issued_slots", l: "Slots issued" },
                { k: "redeemed_slots", l: "Redeemed" },
                { k: "available_slots", l: "Available" },
                { k: "total_users", l: "Users" },
                { k: "total_events", l: "Invites" },
                { k: "published_events", l: "Published" },
                { k: "total_rsvps", l: "RSVPs" },
              ].map(({ k, l }) => (
                <div key={k} data-testid={`admin-stat-${k}`} className="border border-black/10 bg-white p-4">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">{l}</div>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-4xl font-light mt-1 tabular-nums">{stats[k]}</div>
                </div>
              ))}
            </div>

            {/* Revenue */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
              <div data-testid="admin-stat-total_revenue" className="border border-black/10 bg-emerald-50 p-5">
                <div className="text-[10px] tracking-[0.25em] uppercase text-emerald-900">Stripe revenue</div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-5xl font-light mt-1 tabular-nums text-emerald-950">
                  ${stats.total_revenue?.toFixed(2)}
                </div>
              </div>
              <div data-testid="admin-stat-total_purchases" className="border border-black/10 bg-white p-5">
                <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">Purchases</div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-5xl font-light mt-1 tabular-nums">{stats.total_purchases}</div>
              </div>
              <div data-testid="admin-stat-credits_sold" className="border border-black/10 bg-white p-5">
                <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">Credits sold (via Stripe)</div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-5xl font-light mt-1 tabular-nums">{stats.credits_sold}</div>
              </div>
            </div>

            {/* Per-source breakdown */}
            {stats.sources && stats.sources.length > 0 && (
              <div className="mt-6 border border-black/10 bg-white">
                <div className="p-5 border-b border-black/5 text-[10px] tracking-[0.3em] uppercase text-neutral-500">
                  Attribution by note (Etsy listing / batch)
                </div>
                <div className="divide-y divide-black/5">
                  {stats.sources.map((s) => (
                    <div key={s.note} data-testid={`admin-source-${s.note.replace(/\s+/g,"-").toLowerCase()}`} className="p-4 grid grid-cols-6 gap-3 items-center">
                      <div className="col-span-2 truncate">
                        <div className="font-medium text-sm truncate">{s.note}</div>
                        <div className="text-xs text-neutral-500">{s.codes} code{s.codes === 1 ? "" : "s"}</div>
                      </div>
                      <div className="text-sm"><span className="tabular-nums">{s.issued}</span> <span className="text-neutral-500 text-xs">issued</span></div>
                      <div className="text-sm"><span className="tabular-nums">{s.redeemed}</span> <span className="text-neutral-500 text-xs">redeemed</span></div>
                      <div className="col-span-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-neutral-100 overflow-hidden">
                            <div className="h-full bg-neutral-900" style={{ width: `${Math.min(100, s.conversion)}%` }} />
                          </div>
                          <span className="text-xs tabular-nums text-neutral-700 w-12 text-right">{s.conversion}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Settings */}
        <div className="mt-10 border border-black/10 bg-white p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-1">Settings</div>
          <p className="text-xs text-neutral-500 mb-5">Your Etsy listing URL and the sender email for Stripe receipts.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] tracking-[0.25em] uppercase">Etsy shop / listing URL</Label>
              <Input
                data-testid="settings-etsy-url"
                type="url"
                value={settings.etsy_url}
                onChange={(e) => setSettings({ ...settings, etsy_url: e.target.value })}
                placeholder="https://www.etsy.com/shop/your-shop"
                className="rounded-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.25em] uppercase">Receipt sender email</Label>
              <Input
                data-testid="settings-sender-email"
                type="email"
                value={settings.receipt_sender_email}
                onChange={(e) => setSettings({ ...settings, receipt_sender_email: e.target.value })}
                placeholder="onboarding@resend.dev"
                className="rounded-sm mt-1"
              />
              <p className="text-xs text-neutral-500 mt-1">Leave blank to use the .env default. To use a custom domain, verify it in Resend first.</p>
            </div>
          </div>
          <Button onClick={saveSettings} disabled={savingSettings} data-testid="settings-save" className="mt-4 rounded-sm bg-neutral-900 hover:bg-neutral-800">
            <Save size={14} className="mr-2" /> {savingSettings ? "Saving…" : "Save settings"}
          </Button>
        </div>

        {/* Generate */}
        <div className="mt-8 border border-black/10 bg-white p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-4">Generate codes</div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
            <div>
              <Label className="text-[10px] tracking-[0.25em] uppercase">How many</Label>
              <Input data-testid="admin-count" type="number" min={1} max={500} value={count} onChange={(e) => setCount(e.target.value)} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.25em] uppercase">Uses per code</Label>
              <Input data-testid="admin-max-uses" type="number" min={1} max={1000} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} className="rounded-sm mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] tracking-[0.25em] uppercase">Note (e.g. "Etsy listing #123")</Label>
              <Input data-testid="admin-note" value={note} onChange={(e) => setNote(e.target.value)} className="rounded-sm mt-1" placeholder="Etsy April batch" />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={generate} disabled={loading} data-testid="admin-generate" className="rounded-sm bg-neutral-900 hover:bg-neutral-800">
              {loading ? "Generating…" : "Generate"}
            </Button>
            <Button onClick={load} variant="outline" className="rounded-sm"><RefreshCw size={14} className="mr-2" /> Refresh</Button>
          </div>
        </div>

        {/* List */}
        <div className="mt-8 border border-black/10 bg-white">
          <div className="p-5 flex items-center justify-between border-b border-black/5 flex-wrap gap-2">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Code inventory ({codes.length})</div>
            <div className="flex gap-2">
              <Button onClick={copyAll} variant="ghost" size="sm" disabled={codes.length === 0} className="rounded-sm text-xs"><Copy size={14} className="mr-1" /> Copy all</Button>
              <a href="/api/admin/codes/vouchers.zip" data-testid="admin-download-all-vouchers">
                <Button variant="outline" size="sm" disabled={codes.length === 0} className="rounded-sm text-xs"><Archive size={14} className="mr-1" /> Download all PDFs</Button>
              </a>
            </div>
          </div>
          {listLoading ? (
            <div className="p-10 text-neutral-500">Loading…</div>
          ) : codes.length === 0 ? (
            <div className="p-10 text-center text-neutral-500">No codes yet. Generate a batch above.</div>
          ) : (
            <div className="divide-y divide-black/5">
              {codes.map((c) => {
                const exhausted = c.used_count >= c.max_uses;
                let created = "";
                try { created = format(new Date(c.created_at), "MMM d, yyyy"); } catch {}
                return (
                  <div key={c.id} data-testid={`admin-code-row-${c.id}`} className="p-4 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-4 min-w-0">
                      <code className="font-mono text-sm tracking-wider">{c.code}</code>
                      <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 ${exhausted ? "bg-neutral-200 text-neutral-600" : "bg-emerald-100 text-emerald-900"}`}>
                        {c.used_count} / {c.max_uses}
                      </span>
                      {c.note && <span className="text-xs text-neutral-500 truncate max-w-xs">{c.note}</span>}
                      <span className="text-xs text-neutral-400">{created}</span>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => copy(c.code)} data-testid={`admin-copy-${c.id}`} className="p-2 text-neutral-500 hover:text-black" title="Copy code"><Copy size={14} /></button>
                      <a href={`/api/admin/codes/${c.id}/voucher.pdf`} data-testid={`admin-voucher-${c.id}`} className="p-2 text-neutral-500 hover:text-black" title="Download Etsy voucher PDF"><FileDown size={14} /></a>
                      <button onClick={() => revoke(c.id)} data-testid={`admin-revoke-${c.id}`} className="p-2 text-neutral-400 hover:text-red-600" title="Revoke code"><Trash2 size={14} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Production Cleanup */}
        <div data-testid="admin-cleanup" className="bg-white border border-black/10">
          <div className="p-5 border-b border-black/5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Production cleanup</div>
              <p className="text-sm text-neutral-600 mt-1">Wipe test events and reset code inventory before going live on Etsy.</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setCleanupOpen((o) => !o); if (!cleanupOpen) loadAllEvents(); }}
                data-testid="cleanup-toggle"
                className="rounded-sm text-xs"
              >
                <Eraser size={14} className="mr-2" /> {cleanupOpen ? "Hide" : "Open cleanup tools"}
              </Button>
            </div>
          </div>
          {cleanupOpen && (
            <div className="p-5 space-y-6">
              <div>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h3 className="text-sm font-medium">All events ({allEvents.length})</h3>
                  <div className="flex gap-2 items-center">
                    <Input
                      data-testid="cleanup-filter"
                      placeholder="Filter by title or owner email…"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      className="rounded-sm text-xs h-8 w-72"
                    />
                    <Button onClick={loadAllEvents} variant="ghost" size="sm" disabled={cleanupLoading} className="rounded-sm text-xs"><RefreshCw size={12} className="mr-1" /> Refresh</Button>
                  </div>
                </div>
                {cleanupLoading ? (
                  <div className="text-xs text-neutral-500">Loading…</div>
                ) : (
                  <div className="border border-black/10 max-h-[480px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[#FBF9F4] sticky top-0">
                        <tr className="text-left">
                          <th className="px-3 py-2 font-medium tracking-wider uppercase text-[10px] text-neutral-500">Title</th>
                          <th className="px-3 py-2 font-medium tracking-wider uppercase text-[10px] text-neutral-500">Owner</th>
                          <th className="px-3 py-2 font-medium tracking-wider uppercase text-[10px] text-neutral-500">Status</th>
                          <th className="px-3 py-2 font-medium tracking-wider uppercase text-[10px] text-neutral-500 text-right">RSVPs</th>
                          <th className="px-3 py-2 font-medium tracking-wider uppercase text-[10px] text-neutral-500">Created</th>
                          <th className="px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {allEvents
                          .filter((e) => {
                            const f = filter.toLowerCase().trim();
                            if (!f) return true;
                            return (e.title || "").toLowerCase().includes(f) || (e.owner_email || "").toLowerCase().includes(f);
                          })
                          .map((e) => (
                          <tr key={e.id} className="border-t border-black/5 hover:bg-[#FBF9F4]/50">
                            <td className="px-3 py-2 font-medium" title={e.slug}>{e.title}</td>
                            <td className="px-3 py-2 text-neutral-600">{e.owner_email}</td>
                            <td className="px-3 py-2"><span className={`text-[10px] tracking-wider uppercase ${e.status === "published" ? "text-emerald-700" : "text-neutral-500"}`}>{e.status}</span></td>
                            <td className="px-3 py-2 text-right tabular-nums">{e.rsvp_count}{e.guestbook_count > 0 ? <span className="text-neutral-400"> · {e.guestbook_count} gb</span> : null}</td>
                            <td className="px-3 py-2 text-neutral-500">{(() => { try { return format(new Date(e.created_at), "d MMM yyyy"); } catch { return ""; } })()}</td>
                            <td className="px-3 py-2 text-right">
                              <button
                                onClick={() => adminDeleteEvent(e.id, e.title)}
                                data-testid={`cleanup-delete-${e.id}`}
                                className="text-neutral-400 hover:text-red-600 p-1"
                                title="Delete this event and all its data"
                              >
                                <Trash2 size={13} />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {allEvents.length === 0 && (
                          <tr><td colSpan={6} className="px-3 py-6 text-center text-neutral-400">No events</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="border border-red-200 bg-red-50/50 p-4">
                <h3 className="text-sm font-medium text-red-900">Reset all unused codes</h3>
                <p className="text-xs text-red-700 mt-1">
                  Wipes every redemption code that hasn&apos;t been redeemed yet. Already-redeemed codes are preserved (they&apos;re your sales record). Use this once before issuing your first real Etsy code, then maintain your code spreadsheet from there.
                </p>
                <Button
                  onClick={wipeUnusedCodes}
                  data-testid="cleanup-wipe-codes"
                  variant="outline"
                  size="sm"
                  className="mt-3 rounded-sm text-xs border-red-300 text-red-700 hover:bg-red-100"
                >
                  <Trash2 size={13} className="mr-1" /> Delete all unused codes
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
